import {type EditorPart as MachineDevice,type PartKind as DeviceKind} from './parts'
import {nearestSlingCover} from '@core/machine/slingCovers'
// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The device palette (M9-02, 11 §3.1).
//
// Sixteen kinds, each with a working device behind it. The entries are not icons that open a form —
// clicking one **places a real device**, sized and shaped like the thing it is named after, and the
// author moves it and tunes it afterwards. A palette that produced a half-filled object the author
// then had to complete before anything would validate is a palette that makes every placement a
// chore, and the chore is the same one every time.
//
// **Every prototype validates on its own.** The numbers here are inside `validate.ts`'s ranges
// deliberately and by construction — a test places one of each and runs the real validator over the
// result, so a prototype that drifts outside a range fails here rather than at publish time, which
// is a milestone away. They are also *plausible*: a pop bumper is 32 mm because a real skirt is
// about that, a spinner vane weighs 4 g, a slingshot rubber is 60 mm. An author who places one and
// plays it should find a machine that behaves, not a machine that needs tuning before it does
// anything at all.
//
// **Two kinds cannot be placed into an empty machine, and they say so rather than failing.** A
// drop-target bank is a *set of drop targets* — 04 requires at least one and the validator requires
// each to exist — so with no drop targets on the playfield there is nothing for a bank to be. The
// palette disables the entry and names the reason, which is the same disabled-control-that-explains
// -itself pattern the navigation uses for a destination that is not built yet.
//
// Pure: no DOM, no clock, no randomness. An id is derived from what is already there, so placing
// the same device into the same machine twice produces the same two ids in the same order.

import type {   Point2, MachineRule } from '../../core/ts/machine'
import type { PlayfieldSize } from './viewport'

/** The six categories a palette entry belongs to (M13-12). */
export type PaletteCategoryId = 'flippers' | 'bumpers' | 'targets' | 'lanes' | 'ball' | 'fields'

/** A category: what the tray's tile and the 2D palette's group heading say, and which kinds it opens onto. */
export interface PaletteCategory {
  readonly id: PaletteCategoryId
  /** The English label, as the tray draws it (the 2D Builder translates through `builder.category-<id>`). */
  readonly label: string
  /** The kinds in this category, in the order the tray's plinths stand them. */
  readonly kinds: readonly DeviceKind[]
}

/**
 * The six categories, partitioning the palette's sixteen kinds (M13-12).
 *
 * **One table, three readers.** The headset's tray draws one tile per category and one plinth per
 * kind in this order; the 2D Builder's palette groups its buttons under the same headings; and the
 * native `PartsTrayLayout.Categories` is this list kind for kind — `test/xr/build/partsTray.test.ts`
 * reads the C# and holds it so. The order inside a category is the tray's, not the format's: a
 * plinth row reads from the part an author reaches for most to the one they reach for least.
 */
export const CATEGORIES: readonly PaletteCategory[] = [
  { id: 'flippers', label: 'Flippers', kinds: ['flipper'] },
  { id: 'bumpers', label: 'Bumpers & slings', kinds: ['popBumper', 'slingshot', 'flasher'] },
  { id: 'targets', label: 'Targets', kinds: ['standupTarget', 'dropTarget', 'dropTargetBank'] },
  { id: 'lanes', label: 'Lanes & gates', kinds: ['rollover', 'gate', 'ballSaveGate', 'diverter'] },
  { id: 'ball', label: 'Ball handling', kinds: ['kicker', 'plunger', 'autoPlunger', 'captiveBall'] },
  { id: 'fields', label: 'Fields', kinds: ['magnet', 'spinner'] },
]

/** A flipper's side: which way its bat points and swings. */
export type FlipperHand = 'left' | 'right'

/** One plinth's part: a kind, and for a flipper the side it is made for. */
export interface TrayPart {
  readonly kind: DeviceKind
  readonly hand?: FlipperHand
}

/**
 * The parts a category's plinths stand, in order (26 Sep 2026). Every kind is one plinth, except
 * the flipper, which is two — a **left** flipper and a **right** one, each already pointing in
 * from its side — because on the bench a side is a choice made by picking the part up, not a
 * setting found afterwards on a card. `PartsTrayLayout.PartsOf` is the twin.
 */
export function trayPartsOf(category: PaletteCategory): readonly TrayPart[] {
  const parts: TrayPart[] = []
  for (const kind of category.kinds) {
    if (kind === 'flipper') parts.push({ kind, hand: 'left' }, { kind, hand: 'right' })
    else parts.push({ kind })
  }
  return parts
}

/** The category a kind is in. Every kind is in exactly one; the test holds it. */
export function categoryOf(kind: DeviceKind): PaletteCategoryId {
  for (const category of CATEGORIES) if (category.kinds.includes(kind)) return category.id
  throw new Error(`${kind} is in no category`)
}

/** What the palette shows for one kind. */
export interface PaletteEntry {
  readonly kind: DeviceKind
  /** What it is called on the button. Title case, the way a parts catalogue names it. */
  readonly label: string
  /** One line, on the button's `title`. What it does, not what it is made of. */
  readonly hint: string
  /** The category it is grouped under, on the tray and in the 2D palette (M13-12). */
  readonly category: PaletteCategoryId
}

/**
 * The palette, in the order 04 §7 declares the kinds.
 *
 * **04's order rather than an invented one.** It runs roughly from the parts every machine has to
 * the parts only some do, which is a useful order to read, and it means the palette and the format
 * cannot drift into two different vocabularies. A kind added to `DEVICE_KINDS` and not here is a
 * test failure.
 */
export const PALETTE: readonly PaletteEntry[] = [
  {kind:'flasher',label:'Flasher',hint:'A mounted dome lamp. Lights at ball start and flashes on the nearest shot.',category:'bumpers'},
  { kind: 'flipper', label: 'Flipper', hint: 'The bat the player swings. Every machine has two.', category: 'flippers' },
  { kind: 'popBumper', label: 'Pop bumper', hint: 'Kicks the ball away and scores while it does.', category: 'bumpers' },
  { kind: 'slingshot', label: 'Slingshot', hint: 'A rubber that fires the ball back when it is struck hard.', category: 'bumpers' },
  { kind: 'standupTarget', label: 'Standup target', hint: 'A face that scores when hit and stays where it is.', category: 'targets' },
  { kind: 'dropTarget', label: 'Drop target', hint: 'A face that drops out of the playfield when it is hit.', category: 'targets' },
  { kind: 'dropTargetBank', label: 'Drop-target bank', hint: 'A set of drop targets that reset together.', category: 'targets' },
  { kind: 'spinner', label: 'Spinner', hint: 'A vane that spins as the ball passes and scores per turn.', category: 'fields' },
  { kind: 'kicker', label: 'Kicker', hint: 'Holds the ball, then throws it where it is aimed.', category: 'ball' },
  { kind: 'magnet', label: 'Magnet', hint: 'Briefly pulls passing balls, then releases them.', category: 'fields' },
  { kind: 'gate', label: 'Gate', hint: 'A one-way door: the ball passes one way and not the other.', category: 'lanes' },
  { kind: 'rollover', label: 'Rollover', hint: 'An area that scores when the ball rolls over it.', category: 'lanes' },
  { kind: 'captiveBall', label: 'Captive ball', hint: 'A ball in a track, struck by the ball in play.', category: 'ball' },
  { kind: 'plunger', label: 'Plunger', hint: 'Where the ball starts. The player pulls this one.', category: 'ball' },
  { kind: 'autoPlunger', label: 'Auto-plunger', hint: 'A plunger the machine fires itself.', category: 'ball' },
  { kind: 'ballSaveGate', label: 'Ball-save gate', hint: 'A gate that puts a drained ball back in play.', category: 'lanes' },
  { kind: 'diverter', label: 'Diverter', hint: 'Sends the ball down one path or the other.', category: 'lanes' },
]

/** The palette grouped by category, in category order and in each category's own kind order — what the 2D Builder lists. */
export function paletteByCategory(): readonly { readonly category: PaletteCategory; readonly entries: readonly PaletteEntry[] }[] {
  return CATEGORIES.map((category) => ({
    category,
    entries: category.kinds.map((kind) => {
      const entry = PALETTE.find((candidate) => candidate.kind === kind)
      if (entry === undefined) throw new Error(`${kind} is in a category and not in the palette`)
      return entry
    }),
  }))
}

/**
 * A part's name as the author reads it (the 1.0.113 round, 27 Sep 2026: "the component categories
 * have titles in build mode but the components themselves do not have names or titles"): the
 * palette's own label, *Left flipper* / *Right flipper* for a flipper with a side, and *Wire* for a
 * habitrail's card. The tray's plinths and the inspector card's title both say it; the native
 * client's `Palette.LabelOf` is this function.
 */
export function partLabel(kind: DeviceKind | 'wire', hand?: FlipperHand): string {
  if (kind === 'wire') return 'Wire'
  if (kind === 'flipper' && hand === 'left') return 'Left flipper'
  if (kind === 'flipper' && hand === 'right') return 'Right flipper'
  return PALETTE.find((entry) => entry.kind === kind)?.label ?? kind
}

/** One button in the 2D palette: an entry, and for a flipper the side it places. */
export interface PaletteButton {
  readonly entry: PaletteEntry
  readonly hand: FlipperHand | undefined
  readonly label: string
  readonly hint: string
}

/**
 * The palette's buttons by category (26 Sep 2026: "a left orientated flipper and a separate right
 * orientated flipper to choose from"). One button per entry, except the flipper, which is two —
 * *Left flipper* and *Right flipper* — as the headset's tray stands two plinths (`trayPartsOf`).
 */
export function paletteButtonsByCategory(): readonly { readonly category: PaletteCategory; readonly buttons: readonly PaletteButton[] }[] {
  return paletteByCategory().map(({ category, entries }) => ({
    category,
    buttons: entries.flatMap((entry): PaletteButton[] =>
      entry.kind === 'flipper'
        ? [
            { entry, hand: 'left', label: 'Left flipper', hint: 'The bat left of the drain: it points in and swings up to the right.' },
            { entry, hand: 'right', label: 'Right flipper', hint: 'The bat right of the drain: it points in and swings up to the left.' },
          ]
        : [{ entry, hand: undefined, label: entry.label, hint: entry.hint }],
    ),
  }))
}

/**
 * A new id for a device of this kind.
 *
 * `flipper1`, `flipper2`… — the kind and the lowest number nobody is using. Derived rather than
 * random, so two authors placing the same devices in the same order get the same file, and a diff
 * between two versions of a machine is readable.
 */
export function nextId(kind: DeviceKind, taken: ReadonlySet<string>): string {
  for (let n = 1; n <= taken.size + 1; n++) {
    const id = `${kind}${n}`
    if (!taken.has(id)) return id
  }
  // Unreachable: the loop runs one past the number of ids in use, so at least one is free.
  return `${kind}${taken.size + 1}`
}

/**
 * Why this kind cannot be placed right now, or nothing.
 *
 * The one case today is a bank with no drop targets to bank. Answering a *sentence* rather than a
 * boolean, because the control that reads this is a disabled button and a disabled button with no
 * explanation is a control a player presses repeatedly.
 */
export function refusalFor(kind: DeviceKind, devices: readonly MachineDevice[]): string | undefined {
  if (kind !== 'dropTargetBank') return undefined
  const targets = devices.filter((device) => device.kind === 'dropTarget')
  if (targets.length > 0) return undefined
  return 'A bank is a set of drop targets. Place a drop target first.'
}

/** Keep a point inside the playfield, with a margin for the device's own size. */
function inside(at: Point2, playfield: PlayfieldSize, margin: number): Point2 {
  const x = Math.min(Math.max(at[0], -playfield.width / 2 + margin), playfield.width / 2 - margin)
  const y = Math.min(Math.max(at[1], -playfield.length / 2 + margin), playfield.length / 2 - margin)
  return [round(x), round(y)]
}

/** Millimetre precision, so a placed device starts on the grid it will be dragged along. */
function round(metres: number): number {
  return Number(metres.toFixed(3))
}

/**
 * Build one device of this kind, centred on <paramref>at</paramref>.
 *
 * The face-shaped kinds are laid **across** the playfield, because that is the orientation almost
 * every one of them has in a real machine: a drop target faces the player, a slingshot's rubber
 * runs up the inlane, a spinner sits across a lane the ball shoots through. An author who wants a
 * different angle drags one end, and M9-01's handles are already there for it.
 */
export function makeDevice(kind:import('@core/machine').DeviceKind,at:Point2,playfield:PlayfieldSize,existing?:readonly MachineDevice[],hand?:FlipperHand):import('@core/machine').MachineDevice
export function makeDevice(kind:'flasher',at:Point2,playfield:PlayfieldSize,existing?:readonly MachineDevice[],hand?:FlipperHand):import('./parts').FlasherPart
export function makeDevice(kind:DeviceKind,at:Point2,playfield:PlayfieldSize,existing?:readonly MachineDevice[],hand?:FlipperHand):MachineDevice
export function makeDevice(
  kind: DeviceKind,
  at: Point2,
  playfield: PlayfieldSize,
  existing: readonly MachineDevice[] = [],
  hand?: FlipperHand,
): MachineDevice {
  const id = nextId(kind, new Set(existing.map((device) => device.id)))
  const half = 0.03
  const [x, y] = inside(at, playfield, 0.04)
  const a: Point2 = [round(x - half), y]
  const b: Point2 = [round(x + half), y]

  switch (kind) {
    case 'flasher':return {id,kind,position:[x,y],radius:.026,colour:'#FFD9A0'}
    case 'flipper':
      return {
        id,
        kind,
        pivot: [x, y],
        // A real bat is about 76 mm from pivot to tip, tapering from 11 mm to 8 mm.
        length: 0.076,
        radiusPivot: 0.0111,
        radiusTip: 0.0079,
        // 30° below the horizontal at rest and a 60° sweep: a standard lower flipper — pointing
        // in towards the middle from whichever side it is put on (a right bat is the left one
        // mirrored, 210°), so a flipper placed on the right is a right flipper and not a left
        // one swinging the wrong way (26 Sep 2026).
        // The side is the caller's when it says one — the tray's left and right flipper plinths
        // (the owner's round on build 92: "a left orientated flipper and a separate right
        // orientated flipper to choose from") — and otherwise the side of the playfield it lands on.
        restAngle: (hand ?? (x <= 0 ? 'left' : 'right')) === 'left' ? -0.5236 : 3.6652,
        sweep: 1.0472,
        handedness: hand ?? (x <= 0 ? 'left' : 'right'),
      }
    case 'popBumper':
      return { id, kind, centre: [x, y], skirtRadius: 0.032, kickSpeed: 3.4 }
    case 'slingshot':
      return { id, kind, a, b, kickSpeed: 3.2,cover:nearestSlingCover(Math.hypot(b[0]-a[0],b[1]-a[1])) }
    case 'standupTarget':
      return { id, kind, a, b, solidBack:true }
    case 'dropTarget':
      return { id, kind, a, b, solidBack:true }
    case 'dropTargetBank':
      return { id, kind, targets: targetsToBank(existing, [x, y]), autoResetMs: 0 }
    case 'spinner':
      return { id, kind, a, b, vaneMass: 0.004, shaftHeight: 0.03, vaneDrop: 0.02, damping: 0.0004 }
    case 'kicker':
      return {
        id,
        kind,
        centre: [x, y],
        captureRadius: 0.02,
        holdMs: 500,
        // Up the playfield at a shooting speed. The direction is a unit vector; the inspector is
        // where it becomes an angle somebody can dial.
        exit: { direction: [0, 1], speed: 4 },
      }
    case 'plunger':
    case 'autoPlunger':
      return {
        id,
        kind,
        centre: [x, y],
        captureRadius: 0.02,
        // A plunger holds the ball until it is fired rather than for a time. −1 is 04's "until
        // told", and the shooter lane is what a player pulls.
        holdMs: -1,
        exit: { direction: [0, 1], speed: 5.4 },
      }
    case 'magnet':
      return { id, kind, centre: [x, y], radius: 0.075, peakForce: 5.25, releaseAfterMs:120, falloff: 'inverseSquare' }
    case 'gate':
    case 'ballSaveGate':
    case 'diverter':
      // Laid across, so its normal points up the playfield: a gate placed this way lets the ball
      // through going away from the player and stops it coming back, which is what a gate is for.
      return { id, kind, a, b, passDirection: [0, 1] }
    case 'rollover':
      return {
        id,
        kind,
        zRange: [0, 0.02175],
        region: {
          shape: 'rect',
          min: [round(x - 0.02), round(y - 0.03)],
          max: [round(x + 0.02), round(y + 0.03)],
        },
      }
    case 'captiveBall':
      return { id, kind, centre: [x, y], travel: 0.03, direction: [0, 1], cageContacts:true }
  }
}

/** A switch or a coil a device brings with it. */
export interface Wiring {
  readonly switches: readonly { readonly id: string; readonly kind: string; readonly device?: string; readonly perRevolution?: number }[]
  readonly coils: readonly { readonly id: string; readonly kind: string }[]
  readonly rules?: readonly MachineRule[]
}

/**
 * The wiring a device comes with (M9-09).
 *
 * **A device is a thing on the playfield; a switch is how the machine knows it was hit and a coil is
 * how the machine moves it.** 04 keeps them apart, correctly — a rule fires on a *switch*, not on a
 * target — and the consequence is that a device placed with no wiring is a device **no rule can ever
 * mention**. The palette placed exactly that until now: an author could add a drop target, open the
 * rules workspace, and find nothing to point a rule at.
 *
 * So placing a device declares its switch and its coil, named after it, and the device references
 * them. That is what makes 11 §3.2's "pickers populated from the layout" literally true: place a
 * target and its switch is in the list.
 *
 * **Ids are derived from the device's own.** `dropTarget1` brings `dropTarget1Switch` and, where it
 * has one, `dropTarget1Coil` — so a rulesheet reads as the layout does, and removing the device
 * takes its wiring with it without anything having to remember what it was called.
 */
export function wiringFor(device: MachineDevice): Wiring {
  const sw = (extra: Record<string, unknown> = {}): Wiring['switches'][number] =>
    ({ id: `${device.id}Switch`, kind: 'device', device: device.id, ...extra })
  const coil = (kind: string): Wiring['coils'][number] => ({ id: `${device.id}Coil`, kind })

  switch (device.kind) {
    case 'flasher':return {switches:[],coils:[]}
    case 'flipper':
      // The end-of-stroke switch is how the rules know the bat arrived, and the coil is what swings
      // it. The *button* is not here: it belongs to the cabinet, not to one flipper.
      return { switches: [sw()], coils: [coil('flipper')] }
    case 'popBumper':
      return { switches: [sw()], coils: [coil('bumper')] }
    case 'slingshot':
      return { switches: [sw()], coils: [coil('kicker')] }
    case 'kicker':
    case 'plunger':
    case 'autoPlunger':
      return { switches: [sw()], coils: [coil('kicker')] }
    case 'magnet':
      // New magnets arrive with ordinary editable power wiring, retained by history.
      return { switches: [], coils: [coil('magnet')], rules: [{ id: `${device.id}PowerAtBallStart`, on: 'ball:start', do: [{ coil: { id: `${device.id}Coil`, state: 'enable' } }] }] }
    case 'diverter':
      return { switches: [sw()], coils: [coil('diverter')] }
    case 'spinner':
      // A spinner reports every revolution, which is what makes it score per turn rather than once.
      return { switches: [sw({ perRevolution: 1 })], coils: [] }
    case 'standupTarget':
    case 'dropTarget':
    case 'gate':
    case 'ballSaveGate':
    case 'rollover':
    case 'captiveBall':
      return { switches: [sw()], coils: [] }
    case 'dropTargetBank':
      // A bank is its targets. It is hit through them and has no switch of its own.
      return { switches: [], coils: [] }
  }
}

/** The device, wired to what `wiringFor` gives it. */
export function wired<T extends MachineDevice>(device: T): T {
  const wiring = wiringFor(device)
  const record = { ...(device as unknown as Record<string, unknown>) }
  if (wiring.switches.length > 0) record['switch'] = wiring.switches[0]?.id
  if (wiring.coils.length > 0) record['coil'] = wiring.coils[0]?.id
  return record as unknown as T
}

/**
 * Which drop targets a new bank should hold.
 *
 * **The unbanked ones, because that is the workflow**: an author places three drop targets in a row
 * and then banks them, and a bank that arrived empty would be a device they had to go and fill in
 * before anything would validate. When every target is already in a bank the nearest one is taken
 * instead — a target in two banks is legal and occasionally wanted, and it is a far better answer
 * than a bank of nothing, which is not a device at all.
 *
 * Never empty when there is a drop target anywhere, which is what `refusalFor` guarantees the
 * caller has checked.
 */
function targetsToBank(existing: readonly MachineDevice[], at: Point2): readonly string[] {
  const targets = existing.filter((device) => device.kind === 'dropTarget')
  if (targets.length === 0) return []
  const banked = new Set<string>()
  for (const device of existing) {
    if (device.kind === 'dropTargetBank') for (const id of device.targets) banked.add(id)
  }
  const free = targets.filter((device) => !banked.has(device.id))
  if (free.length > 0) return free.map((device) => device.id)

  let nearest = targets[0] as MachineDevice
  let best = Number.POSITIVE_INFINITY
  for (const device of targets) {
    if (device.kind !== 'dropTarget') continue
    const midX = (device.a[0] + device.b[0]) / 2
    const midY = (device.a[1] + device.b[1]) / 2
    const gap = (midX - at[0]) ** 2 + (midY - at[1]) ** 2
    if (gap < best) {
      best = gap
      nearest = device
    }
  }
  return [nearest.id]
}
